import {
  Avatar,
  Button,
  Card,
  Chip,
  Division,
  EmptyState,
  Flex,
  Heading,
  Icons,
  Paragraph,
  Table,
  type TableColumnType,
  type TableColumnVerticalAlign,
} from "@ir/ui";
import { useAutoHeight } from "@src/presentation/hooks/autoheight";
import { useUsersAccessData } from "@src/presentation/providers/UsersAccessDataProvider";
import { useNavigate } from "react-router";
import {
  getStableOrgChipStyles,
  orgChipPaletteKey,
  type UsersDataTableOrgItem,
} from "./UsersDataTableOrgChip";

const verticalAlign: TableColumnVerticalAlign = "middle";
type UserStatusColor = "success" | "warning" | "error" | "grey";

interface UserDataType {
  key: string;
  userId: number;
  user: {
    key: string;
    fullName: string;
    isDeleted: boolean;
    status: {
      label: string;
      color: UserStatusColor;
    };
  };
  email: string;
  organizations: UsersDataTableOrgItem[];
  authIdentity: string;
}

function userStatusFromFlags(
  isDeleted: boolean,
  isActive: boolean,
): { label: string; color: UserStatusColor } {
  let label = "Inactive";
  if (isDeleted) {
    label = "Deleted";
  } else if (isActive) {
    label = "Active";
  }

  let color: UserStatusColor = "warning";
  if (isDeleted) {
    color = "error";
  } else if (isActive) {
    color = "success";
  }

  return {
    label,
    color,
  };
}

function buildColumns(
  onUserOpen: (userId: number) => void,
): TableColumnType<UserDataType>[] {
  return [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      align: "left",
      verticalAlign,
      className: "ps-3",
      cellRender: (value: unknown, record: UserDataType) => {
        const user = value as UserDataType["user"];
        return (
          <Flex direction="row" align="center" gap={0.5} className="ps-1">
            <Flex direction="row" align="center" gap={0.25}>
              <Avatar size="extra-small" name={user.fullName} boxShadow />
              <Flex direction="row" align="center" gap={0.25}>
                <Button
                  variant="clear"
                  className="py-1 px-2 text-primary"
                  style={{ fontSize: "0.85rem" }}
                  onPress={() => onUserOpen(record.userId)}
                >
                  {user.fullName}
                </Button>
                <Chip
                  size="small"
                  borderless
                  color={user.status.color}
                  label={user.status.label}
                />
              </Flex>
            </Flex>
          </Flex>
        );
      },
    },

    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "left",
      verticalAlign,
      cellRender: (value: unknown) => {
        return (
          <Flex
            className="m-0 text-grey-500"
            style={{ whiteSpace: "pre-wrap !important" }}
          >
            {value as string}
          </Flex>
        );
      },
    },
    {
      title: "Organization",
      dataIndex: "organizations",
      key: "organization",
      align: "left",
      verticalAlign,
      cellRender: (value: unknown) => {
        const orgs = (value as UserDataType["organizations"]) ?? [];
        if (orgs.length === 0) {
          return <Paragraph className="m-0 text-grey-500">-</Paragraph>;
        }
        return (
          <Flex wrap="wrap" gap={0.25}>
            {orgs.map((org) => (
              <Chip
                key={orgChipPaletteKey(org)}
                label={org.name}
                size="small"
                color="grey"
                borderless
                style={getStableOrgChipStyles(org)}
              />
            ))}
          </Flex>
        );
      },
    },
    {
      title: "Auth ID",
      dataIndex: "authIdentity",
      key: "authIdentity",
      align: "left",
      verticalAlign,
    },
  ];
}

export const UsersDataTable = () => {
  const navigate = useNavigate();
  const { users, search, setSearch, setPage } = useUsersAccessData();
  const { ref: tableScrollContainerRef, height: tableHeight } =
    useAutoHeight<HTMLDivElement>();
  const columns = buildColumns((userId) =>
    navigate(`/users-access/${userId}`),
  );

  const dataSource: UserDataType[] = users.map((adminUser) => {
    const organizations =
      adminUser.organizations
        ?.map((o) => ({
          id: o.organizationId,
          name: (o.organizationName ?? "").trim(),
        }))
        .filter((o) => o.name.length > 0) ?? [];

    const isDeleted = Boolean(adminUser.isDeleted);
    const isActive = Boolean(adminUser.isActive);
    const userStatus = userStatusFromFlags(isDeleted, isActive);

    return {
      key: String(adminUser.userId ?? adminUser.sub),
      userId: adminUser.userId ?? 0,
      user: {
        key: String(adminUser.userId ?? adminUser.sub),
        fullName: adminUser.fullName,
        isDeleted,
        status: userStatus,
      },
      email: adminUser.email,
      organizations,
      authIdentity: adminUser.sub,
    };
  });

  const trimmedSearch = search.trim();
  const hasActiveSearch = trimmedSearch.length > 0;

  const clearSearch = () => {
    setPage(1);
    setSearch("");
  };

  if (dataSource.length === 0) {
    return (
      <Card className="bg-white" variant="outlined" shadow>
        {hasActiveSearch ? (
          <EmptyState
            className="px-3 py-4"
            icon={Icons.InfoCircle}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-2">
                No results found for &quot;{trimmedSearch}&quot;
              </Heading>
            }
            description={
              <>
                <Paragraph className="text-grey-500 font-regular mb-0">
                  Try adjusting your search terms, or clear the search to see
                  all users.
                </Paragraph>
                <Button className="mt-3" variant="clear" onPress={clearSearch}>
                  Clear search
                </Button>
              </>
            }
          />
        ) : (
          <EmptyState
            className="px-3 py-4"
            icon={Icons.UserManagement}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-2">
                No users found
              </Heading>
            }
            description={
              <Paragraph className="text-grey-500 font-regular mb-0">
                There are no users to display across organizations.
              </Paragraph>
            }
          />
        )}
      </Card>
    );
  }

  return (
    <Card
      className="bg-white"
      variant="outlined"
      shadow
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Division
        ref={tableScrollContainerRef}
        style={{ flex: 1, minHeight: 0, minWidth: 0 }}
      >
        <Table<UserDataType>
          columns={columns}
          dataSource={dataSource}
          rowHoverable={true}
          aria-label="Users Table"
          aria-labelledby="users-table"
          sticky={true}
          scroll={{ x: "100%", y: tableHeight }}
        />
      </Division>
    </Card>
  );
};
